import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { User as UserIcon, Save, Lock, ArrowRight, Mail, Phone, IdCard, CalendarOff, AlertTriangle, Plus, Wallet, Download } from "lucide-react";
import { toast } from "sonner";

// Roles for which we expose "My Leaves" + "Apply for Leave" + "My Violations" sections.
const EMPLOYEE_ROLES = ["teacher", "academic", "finance", "hr", "school_admin", "driver"];

export default function ProfilePage() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [pwd, setPwd] = useState({ current: "", new1: "", new2: "" });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  // Employee self-service
  const [empProfile, setEmpProfile] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [myViolations, setMyViolations] = useState([]);
  const [myPayslips, setMyPayslips] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: "annual", start_date: "", end_date: "", reason: "" });

  const isEmployee = user && EMPLOYEE_ROLES.includes(user.role);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  // Load my employee record + leaves + violations + payslips
  const loadEmployeeSelf = async () => {
    if (!user || !isEmployee) return;
    try {
      const { data: mine } = await api.get("/employees/me");
      setEmpProfile(mine || null);
      const lr = await api.get("/leave-requests");
      setMyLeaves(lr.data || []);
      const vr = await api.get("/violations");
      setMyViolations(vr.data || []);
      const ps = await api.get("/payroll/me?limit=6");
      setMyPayslips(ps.data || []);
    } catch (err) { console.warn("loadEmployeeSelf failed:", err?.message || err); }
  };
  useEffect(() => { loadEmployeeSelf(); }, [user?.id, leaveOpen]);

  const submitLeave = async () => {
    if (!empProfile?.id) { toast.error(t("not_found_emp_profile")); return; }
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error(t("pick_dates")); return; }
    try {
      await api.post("/leave-requests", { ...leaveForm, employee_id: empProfile.id });
      toast.success(t("leave_sent"));
      setLeaveOpen(false);
      setLeaveForm({ leave_type: "annual", start_date: "", end_date: "", reason: "" });
    } catch (e) { toast.error(e.response?.data?.detail || t("leave_send_failed")); }
  };

  // Download payslip PDF (axios with responseType blob + auth header is already attached by api instance)
  const downloadPayslip = async (item) => {
    try {
      const resp = await api.get(`/payroll/${item.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in new tab — works on both desktop browser and WebView (user can then save via browser menu)
      const w = window.open(url, "_blank");
      if (!w) {
        // Popup blocked → trigger download instead
        const a = document.createElement("a");
        a.href = url;
        a.download = `payslip_${item.period || item.id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Revoke after a short delay to give browser time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(t("payslip_load_failed"));
    }
  };

  const periodArabic = (p) => {
    const months = { "01":"يناير","02":"فبراير","03":"مارس","04":"أبريل","05":"مايو","06":"يونيو","07":"يوليو","08":"أغسطس","09":"سبتمبر","10":"أكتوبر","11":"نوفمبر","12":"ديسمبر" };
    const [y, m] = (p || "").split("-");
    return `${months[m] || m} ${y || ""}`;
  };
  const totalAmount = (it) => {
    const base = +(it.base_salary || 0);
    const ot = +(it.overtime || 0);
    const allow = Object.values(it.allowances || {}).reduce((a, b) => a + (+b || 0), 0);
    const deduct = Object.values(it.deductions || {}).reduce((a, b) => a + (+b || 0), 0);
    return +(it.net_total ?? base + ot + allow - deduct).toFixed(2);
  };

  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error(t("name_required")); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/users/${user.id}`, { name: form.name, phone: form.phone });
      if (setUser) setUser({ ...user, name: form.name, phone: form.phone });
      toast.success(t("success"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const savePassword = async () => {
    if (!pwd.new1 || pwd.new1.length < 6) { toast.error(t("pw_too_short_long")); return; }
    if (pwd.new1 !== pwd.new2) { toast.error(t("pw_mismatch")); return; }
    setSavingPw(true);
    try {
      await api.put(`/users/${user.id}`, { password: pwd.new1 });
      toast.success(t("pw_updated"));
      setPwd({ current: "", new1: "", new2: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSavingPw(false);
  };

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="profile-back">
          <ArrowRight className="w-5 h-5 lucide-rotate-rtl" />
        </Button>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <UserIcon className="w-7 h-7 text-primary" /> {t("profile") || "ملفي الشخصي"}
        </h1>
      </div>

      {/* Avatar + read-only fields */}
      <Card className="p-6 border shadow-none">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary grid place-items-center text-2xl font-bold">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="text-lg font-semibold">{user.name || "—"}</div>
            <div className="text-xs text-muted-foreground font-mono">{user.email}</div>
            <div className="text-xs text-primary mt-1 font-medium">{t(user.role) || user.role}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className="truncate font-mono text-xs">{user.email}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm">
            <IdCard className="w-4 h-4 text-muted-foreground" />
            <span className="truncate">{t(user.role) || user.role}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>{t("name") || "الاسم"}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <Label>{t("phone") || "الجوال"}</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+9665..." data-testid="profile-phone" />
          </div>
        </div>

        <Button onClick={saveProfile} disabled={saving} className="w-full mt-4" data-testid="profile-save">
          <Save className="w-4 h-4 me-2" /> {saving ? "..." : t("save")}
        </Button>
      </Card>

      {/* Password */}
      <Card className="p-6 border shadow-none">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-primary" /> {t("update_password")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>{t("new_password")}</Label>
            <Input type="password" value={pwd.new1} onChange={(e) => setPwd({ ...pwd, new1: e.target.value })} data-testid="profile-pw-new" />
          </div>
          <div>
            <Label>{t("confirm_password")}</Label>
            <Input type="password" value={pwd.new2} onChange={(e) => setPwd({ ...pwd, new2: e.target.value })} data-testid="profile-pw-confirm" />
          </div>
        </div>
        <Button onClick={savePassword} disabled={savingPw} variant="secondary" className="w-full mt-4" data-testid="profile-pw-save">
          <Lock className="w-4 h-4 me-2" /> {savingPw ? t("loading") : t("update_password")}
        </Button>
      </Card>

      {/* Employee-only: My Leaves + Apply for Leave */}
      {isEmployee && (
        <Card className="p-6 border shadow-none" data-testid="my-leaves-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-primary" /> {t("my_leaves_title")}
            </h2>
            <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={!empProfile?.id} data-testid="apply-leave-btn">
                  <Plus className="w-4 h-4 me-2" /> {t("apply_leave_btn")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("apply_leave_dialog")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t("leave_type")}</Label>
                    <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                      <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="annual">{t("leave_annual")}</SelectItem>
                        <SelectItem value="sick">{t("leave_sick")}</SelectItem>
                        <SelectItem value="emergency">{t("leave_emergency")}</SelectItem>
                        <SelectItem value="unpaid">{t("leave_unpaid")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("leave_from")}</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} data-testid="leave-start" /></div>
                    <div><Label>{t("leave_to")}</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} data-testid="leave-end" /></div>
                  </div>
                  <div><Label>{t("leave_reason")}</Label><Textarea rows={3} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} data-testid="leave-reason" /></div>
                  <Button onClick={submitLeave} className="w-full" data-testid="submit-leave-btn">{t("submit_leave")}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {!empProfile?.id && (
            <div className="text-xs text-muted-foreground py-4">
              {t("emp_no_profile")}
            </div>
          )}
          {myLeaves.length === 0 ? (
            empProfile?.id && <div className="text-xs text-muted-foreground py-4">{t("no_leaves_yet")}</div>
          ) : (
            <div className="space-y-2">
              {myLeaves.slice(0, 10).map((l) => (
                <div key={l.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 text-sm" data-testid={`leave-${l.id}`}>
                  <div>
                    <div className="font-medium">{t(`leave_${l.leave_type}`)}</div>
                    <div className="text-xs text-muted-foreground font-mono">{l.start_date} → {l.end_date}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    l.status === "approved" ? "bg-primary/10 text-primary" :
                    l.status === "rejected" ? "bg-destructive/10 text-destructive" :
                    "bg-amber-500/10 text-amber-700"
                  }`}>
                    {t(`leave_${l.status}`)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Employee-only: My Payslips (last 6, PDF download) */}
      {isEmployee && (
        <Card className="p-6 border shadow-none" data-testid="my-payslips-card">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5 text-primary" /> {t("my_payslips_title")}
          </h2>
          {myPayslips.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">{t("no_payslips")}</div>
          ) : (
            <div className="space-y-2">
              {myPayslips.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 text-sm" data-testid={`payslip-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{periodArabic(p.period)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("payslip_net")}: <span className="font-mono font-semibold text-primary">{totalAmount(p).toLocaleString()} ر.س</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPayslip(p)}
                    data-testid={`payslip-download-${p.id}`}
                  >
                    <Download className="w-4 h-4 me-1" />
                    PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Employee-only: My Violations (read-only) */}
      {isEmployee && (
        <Card className="p-6 border shadow-none" data-testid="my-violations-card">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> {t("my_violations_title")}
          </h2>
          {myViolations.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">{t("no_violations")}</div>
          ) : (
            <div className="space-y-2">
              {myViolations.slice(0, 10).map((v) => (
                <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 text-sm" data-testid={`vio-${v.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{v.description || v.type || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono">{v.date || (v.created_at || "").slice(0, 10)}</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 font-medium">
                    {v.severity || v.type || "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
